import type { ReactElement, Ref } from 'react';
import React, { forwardRef, useCallback, useMemo, useRef } from 'react';
import type { ClassNameProp } from '../../common-types';
import type { RenderLayerProps } from 'react-laag';
import type {
  UseComboboxActions,
  UseComboboxPropGetters,
  UseMultipleSelectionActions,
  UseMultipleSelectionPropGetters
} from 'downshift';
import _ from 'lodash';
import { Icon, LoadMask, Tool } from '../../basic';
import { isEmptyArray, isEmptyString, mergeRefs } from '../../../util';
import type { CommonInputProps } from '../CommonInputProps';
import type { TextInputProps } from '../TextInput';
import { intl, intlFmt, useTooltipDefault } from '../../../lib';
import type { Placement } from 'react-laag/dist/ToggleLayer/types';
import { ValidationErrorMessage } from './ValidationErrorMessage';

/**
 * Default ToggleLayer {@link Placement} for Combobox elements.
 * @author agd
 */
export const DEFAULT_COMBOBOX_LAYER_PLACEMENT: Placement = {
  anchor: 'BOTTOM_LEFT',
  autoAdjust: true,
  snapToAnchor: true,
  possibleAnchors: ['BOTTOM_LEFT', 'TOP_LEFT']
};

/**
 * Props that are needed by all components in this package.
 *
 * @author agd
 */
interface ComboboxComponentProps<T>
  extends ClassNameProp,
    Pick<RenderLayerProps, 'isOpen'> {
  /**
   * All the selected items that need to be highlighted as such.
   */
  selectedItems: Array<T>;
  /**
   * Switch to hide the clearIcon permanently.
   */
  hideClearIcon?: boolean;
}

/**
 * Properties for a {@link ComboboxDropdownMenu} component.
 *
 * @author agd
 */
export interface ComboboxDropdownMenuProps<T>
  extends ComboboxComponentProps<T>,
    Pick<RenderLayerProps, 'layerProps' | 'triggerRect'>,
    Pick<UseComboboxPropGetters<T>, 'getItemProps' | 'getMenuProps'> {
  /**
   * If the items are loading (e.g. when doing an ajax call for loading the items).
   */
  loading: boolean;
  /**
   * If items have been loaded or not.
   * (If items are not loaded asyncronously, this can always be set to true)
   */
  wasLoaded: boolean;
  /**
   * The selectable items.
   */
  selectableItems: Array<T>;
  /**
   * Index of the item that is highlighted at the moment.
   */
  highlightedIndex: number | undefined;
  /**
   * Mapping function for `Item` to distinguish items.
   * @param item the item to map
   */
  itemToKey: (item: T) => string;
  /**
   * Function for rendering the items in a human-readable way.
   * @param item the item to map
   */
  renderItem: (item: T) => React.ReactNode;
  /**
   * Render Function for rendering the state when no entries have been found for.
   * (when filtering or querying)
   */
  renderEmptyItems?: () => React.ReactNode;
}

/**
 * React-laag + downshift menu for a Combobox element.
 *
 * @param properties a {@link ComboboxDropdownMenuProps} object.
 * @return the created {@link JSX.Element}.
 * @author agd
 * @constructor
 */
export function ComboboxDropdownMenu<T>({
  isOpen,
  layerProps,
  triggerRect,
  loading,
  highlightedIndex,
  selectableItems,
  selectedItems,
  itemToKey,
  renderItem,
  renderEmptyItems,
  getItemProps,
  getMenuProps,
  className = '',
  wasLoaded
}: ComboboxDropdownMenuProps<T>): JSX.Element {
  /*
   * Callback method for the rendered menu content.
   * This exists because multiple ifs through a ternary operator is just annoying and hard to read.
   */
  const getMenuContent = useCallback(() => {
    if (loading) {
      // loadmask to indicate that something is being loaded
      return <LoadMask masked={true} className="downshift-menu-loadmask" />;
    } else if (wasLoaded && isEmptyArray(selectableItems)) {
      // indicator that nothing was found (e.g. due to filtering/searching)
      return (
        renderEmptyItems?.() ?? (
          <li aria-disabled={true} className="no-results">
            {intl('common.search.noentry')}
          </li>
        )
      );
    } else {
      return selectableItems.map((item, index) => (
        <li
          key={itemToKey(item)}
          className={`menu-item ${
            highlightedIndex === index ? 'highlighted' : ''
          } ${selectedItems.includes(item) ? 'selected' : ''}`.trim()}
          {...getItemProps({
            key: itemToKey(item),
            index,
            item
          })}
        >
          {renderItem(item)}
        </li>
      ));
    }
  }, [
    getItemProps,
    highlightedIndex,
    itemToKey,
    loading,
    renderItem,
    selectableItems,
    selectedItems,
    wasLoaded,
    renderEmptyItems
  ]);
  return (
    <div
      className={`ehs-menu ehs-select-menu ${className}`.trim()}
      {...layerProps}
      ref={layerProps.ref}
      style={{
        ...layerProps.style,
        width: triggerRect?.width,
        // Downshift needs the dropdown to always be rendered
        display:
          !isOpen || (isEmptyArray(selectableItems) && !loading && !wasLoaded)
            ? 'none'
            : 'inherit'
      }}
    >
      <ul className="downshift-menu" {...getMenuProps()}>
        {getMenuContent()}
      </ul>
    </div>
  );
}

/**
 * Props for a {@link ComboboxInputElement} component.
 *
 * @author agd
 */
export interface ComboboxInputElementProps<T>
  extends ComboboxComponentProps<T>,
    Pick<
      UseComboboxPropGetters<T>,
      'getLabelProps' | 'getComboboxProps' | 'getInputProps'
    >,
    Partial<Pick<UseComboboxPropGetters<T>, 'getToggleButtonProps'>>,
    Partial<Pick<UseMultipleSelectionPropGetters<T>, 'getDropdownProps'>>,
    Pick<
      CommonInputProps<T>,
      'error' | 'label' | 'disabled' | 'required' | 'labelOnTop' | 'readOnly'
    >,
    Pick<TextInputProps, 'placeholder'>,
    Pick<UseComboboxActions<T>, 'toggleMenu' | 'openMenu'>,
    Partial<Pick<UseMultipleSelectionActions<T>, 'removeSelectedItem'>> {
  /**
   * ToggleLayer triggerRef type, copied because they dont' export it.
   */
  triggerRef: React.RefObject<any>;
  /**
   * Mapping function for `Item` to map an item into a simple string format.
   * This is used to show the selected items in a multiselect scenario.
   *
   * @param item the item to map
   */
  itemToString?: (item: T) => string;
  /**
   * Selection variant.
   */
  variant: 'single' | 'multi';
  /**
   * If the search icon on the left side should be shown or not.
   * Generally, you only want either the search icon or the dropdown tool.
   */
  showSearchIcon: boolean;
  /**
   * If the expand tool on the right side should be shown.
   * Generally, you only want either the search icon or the dropdown tool.
   */
  showExpandTool: boolean;
  /**
   * How clicking onto the element should behave.
   * This only affect clicking onto the element itself, the expandTool always uses the 'toggle' behavior.
   */
  elementClickBehavior: 'openMenu' | 'toggleMenu';
  /**
   * Handler for when the input element's value changes.
   * @param value the new value
   */
  onInputChange: (value: string) => void;

  /**
   * Max number of shown selected items. When a user selects more, additionally to selected items, there will be shown
   * just a number indicating how many more items are selected.
   */
  tagLimit?: number;
}

export type ComboboxInputPropsWithStandardRef<
  T
> = ComboboxInputElementProps<T> & {
  ref?: Ref<HTMLInputElement>;
};

type ComboboxInputPropsWithForwardRef<T> = ComboboxInputElementProps<T> & {
  forwardedRef: Ref<HTMLInputElement>;
};

/**
 * Combobox form element content.
 * This assumes that Downshift's `useCombobox` (and optionally also `useMultipleSelection`) is used with it.
 *
 * @param properties a {@link ComboboxInputElementProps} object.
 * @return the created {@link JSX.Element}.
 * @author agd
 * @constructor
 */
function ComboboxInputComponent<T>({
  triggerRef,
  selectedItems,
  error,
  label,
  required,
  disabled,
  readOnly,
  isOpen,
  getComboboxProps,
  getLabelProps,
  getInputProps,
  getDropdownProps,
  itemToString,
  variant,
  toggleMenu,
  openMenu,
  removeSelectedItem,
  placeholder,
  className,
  showSearchIcon,
  hideClearIcon = false,
  showExpandTool,
  elementClickBehavior,
  getToggleButtonProps,
  onInputChange,
  tagLimit,
  forwardedRef,
  labelOnTop = false
}: ComboboxInputPropsWithForwardRef<T>): JSX.Element {
  const inputHintId = useMemo(() => {
    return placeholder ? _.uniqueId('ehs-combobox-input-hint-') : undefined;
  }, [placeholder]);

  const multiSelectScreenReaderHelpTextId = useRef(
    variant === 'multi' ? _.uniqueId('screen-reader-only') : undefined
  );

  const inputRef = useRef<HTMLInputElement>(null);
  // callback to focus the input element
  const focusInputEl = useCallback(() => {
    inputRef?.current?.focus();
  }, []);

  // callbacks for opening / toggling the menu
  const toggleMenuCallback = useCallback(() => {
    toggleMenu();
    focusInputEl();
  }, [focusInputEl, toggleMenu]);
  const openMenuCallback = useCallback(() => {
    openMenu();
    focusInputEl();
  }, [focusInputEl, openMenu]);

  // element props
  const comboBoxProps = getComboboxProps();
  const inputProps = getInputProps({
    ...getDropdownProps?.({ preventKeyAction: isOpen }),
    // overriding default onChange to get around a Downshift bug (https://github.com/downshift-js/downshift/issues/1108)
    // TODO: remove the workaround once Downshift fixed the issue on their side
    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
      onInputChange(event.target.value)
  });
  const showClearIcon =
    !hideClearIcon &&
    variant === 'single' &&
    !disabled &&
    !readOnly &&
    !isEmptyArray(
      selectedItems.filter(
        e => !Object.values(e as Record<string, unknown>).every(el => el == '')
      )
    );
  // build root element className
  const elementClasses = getElementClasses(
    !!error,
    selectedItems.length === 0 && isEmptyString(inputProps.value),
    isOpen,
    !!disabled,
    showExpandTool,
    showSearchIcon,
    showClearIcon,
    labelOnTop
  );

  // making sure tag limit is a positive number
  tagLimit = tagLimit && tagLimit > 0 ? tagLimit : undefined;

  const [tooltipCmp, tooltipProps] = useTooltipDefault(
    selectedItems
      .slice(tagLimit)
      .map(item => {
        return itemToString?.(item);
      })
      .join(', '),
    {
      fixed: true
    }
  );

  const onClear = () => {
    removeSelectedItem?.(selectedItems[0]);
  };

  const ariaLabeledIds = [
    inputProps['aria-labelledby'],
    multiSelectScreenReaderHelpTextId.current
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={`ehs-input-field-wrapper ${className ?? ''} ${
        labelOnTop ? 'labelontop' : ''
      }`.trim()}
    >
      {variant === 'multi' && (
        <div
          id={multiSelectScreenReaderHelpTextId.current}
          className="screen-reader-only"
        >
          {selectedItems
            .map(i => itemToString?.(i))
            .filter(Boolean)
            .join(', ')}
        </div>
      )}
      {labelOnTop && (
        <label className="label" {...getLabelProps()}>
          {required ? `${label} *` : label}
        </label>
      )}
      <div
        {...comboBoxProps}
        className={elementClasses.join(' ')}
        ref={mergeRefs([triggerRef, comboBoxProps.ref])}
        onClick={
          elementClickBehavior === 'openMenu'
            ? openMenuCallback
            : toggleMenuCallback
        }
        role={undefined}
        aria-expanded={undefined}
      >
        <div className="select-input-content">
          {showSearchIcon && (
            <Icon
              aria-label={undefined}
              iconName="search"
              className="search-icon"
            />
          )}
          <div className="select-input-inner">
            {variant === 'multi' &&
              selectedItems.slice(0, tagLimit).map((item, index) => (
                <div
                  key={label + index}
                  className="selected-element"
                  aria-selected={true}
                >
                  <span>{itemToString?.(item)}</span>
                  <Tool
                    icon="materialicon-close"
                    onClick={() => removeSelectedItem?.(item)}
                    tooltip={intlFmt(
                      'common.removeitem',
                      itemToString?.(item) ?? ''
                    )}
                  />
                </div>
              ))}
            {variant === 'multi' &&
              tagLimit &&
              selectedItems.length > tagLimit && (
                <div
                  key={selectedItems.length}
                  className="selected-element tag-limit"
                >
                  {tooltipCmp}
                  <span {...tooltipProps}>
                    +{selectedItems.length - tagLimit}
                  </span>
                </div>
              )}
            <input
              aria-invalid={!!error}
              aria-required={required}
              aria-describedby={inputHintId}
              aria-readonly={readOnly}
              {...inputProps}
              aria-labelledby={ariaLabeledIds}
              ref={mergeRefs([inputRef, inputProps.ref, forwardedRef])}
              className="input"
              type="text"
              disabled={disabled}
              readOnly={readOnly}
              role="combobox"
              aria-expanded={isOpen}
            />
            {!labelOnTop && (
              <label
                className="label"
                {...getLabelProps()}
                onClick={
                  // label is inside div with onclick event handling and 'for=#id' attribute creates second click event
                  // we need to stop propagation of click event and thus prevent duplicated event firing
                  (event: MouseEvent) => event.stopPropagation()
                }
              >
                {required ? `${label} *` : label}
              </label>
            )}
          </div>
          {error && (
            <Icon
              className="ehs-input-error-icon"
              aria-hidden={true}
              aria-label=""
              iconName="shui_error"
            />
          )}
          {showClearIcon && (
            <Tool
              icon="delete"
              className="clear-tool"
              onClick={onClear}
              tooltip={intl('view.button.clear')}
            />
          )}
          {showExpandTool && !readOnly && (
            <Tool
              {...getToggleButtonProps?.()}
              icon={
                isOpen
                  ? 'materialicon-arrow_drop_up'
                  : 'materialicon-arrow_drop_down'
              }
              className="expand-tool"
              onClick={toggleMenuCallback}
              tooltip={intl(
                isOpen
                  ? 'view.grid.collapse.tooltip'
                  : 'view.grid.expand.tooltip'
              )}
            />
          )}
        </div>
      </div>
      {placeholder && (
        <p
          id={inputHintId}
          className={`ehs-input-hint${disabled ? ' disabled' : ''}`}
        >
          {placeholder}
        </p>
      )}
      {error && <ValidationErrorMessage error={error} />}
    </div>
  );
}

export const ComboboxInputElement: <Item>(
  props: ComboboxInputPropsWithStandardRef<Item>
) => ReactElement | null = forwardRef(function ComboboxInputElement(
  props,
  ref
) {
  return <ComboboxInputComponent forwardedRef={ref} {...props} />;
});

/**
 * Helper function that builds class names for the combobox element.
 *
 * @param isValid true if selection is valid
 * @param isEmpty true if nothing is selected
 * @param isOpen true if the combobox is opened
 * @param disabled true if the combobox is disabled
 * @param showExpandTool see {@link ComboboxDropdownMenuProps#showExpandTool}
 * @param showSearchIcon see {@link ComboboxDropdownMenuProps#showSearchIcon}
 * @param showClearIcon see {@link ComboboxDropdownMenuProps#showClearIcon}
 * @param labelOnTop true if labelontop is set
 * @return array of css classes
 */
function getElementClasses(
  isValid: boolean,
  isEmpty: boolean,
  isOpen: boolean,
  disabled: boolean,
  showExpandTool: boolean,
  showSearchIcon: boolean,
  showClearIcon: boolean,
  labelOnTop: boolean
): string[] {
  const elementClasses = [
    'ehs-input-field',
    'ehs-select-input-field',
    isValid ? 'invalid' : 'valid'
  ];

  // build css classes
  if (isEmpty) elementClasses.push('value-empty');
  if (isOpen) elementClasses.push('focused');
  if (disabled) elementClasses.push('disabled');
  if (showExpandTool) elementClasses.push('has-expand-tool');
  if (showSearchIcon) elementClasses.push('has-search-icon');
  if (showClearIcon) {
    elementClasses.push('has-clear-tool');
  }
  if (labelOnTop) {
    elementClasses.push('labelontop');
  }

  return elementClasses;
}
